import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  Sparkles,
  Loader2,
  Download,
  Copy,
  CheckCircle,
  Calendar,
  ChevronRight,
  ChevronLeft,
  TrendingUp,
  Briefcase,
  Users,
  Target,
  Lightbulb,
  FileText
} from 'lucide-react';

interface Resume {
  id: string;
  filename: string;
  created_at: string;
}

interface CareerAnalysis {
  id: string;
  years_experience: number;
  industries: string[];
  leadership_roles: any[];
  areas_of_expertise: string[];
  technical_skills: string[];
  soft_skills: string[];
  career_trajectory: string;
  unique_value_proposition: string;
  seniority_level: string;
}

interface CampaignSuggestion {
  campaign_name: string;
  rationale: string;
  expertise_area: string;
  target_audience: string;
  key_themes: string[];
}

interface Post {
  week: number;
  day: string;
  post: string;
  hashtags: string[];
}

const CAREER_FOCUS_OPTIONS = [
  { id: 'project_work', label: 'Project Work & Execution', icon: Briefcase },
  { id: 'team_leadership', label: 'Leading Teams', icon: Users },
  { id: 'staff_management', label: 'Managing Staff', icon: Target },
  { id: 'strategic_planning', label: 'Strategic Planning', icon: TrendingUp },
  { id: 'technical_execution', label: 'Technical Execution', icon: FileText },
  { id: 'innovation', label: 'Innovation & R&D', icon: Lightbulb },
  { id: 'client_relationships', label: 'Client Relationships', icon: Users },
  { id: 'business_development', label: 'Business Development', icon: TrendingUp },
];

export default function ContentCampaigns() {
  const { session } = useAuth();
  const [step, setStep] = useState(1);
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState('');

  const [analyzingResume, setAnalyzingResume] = useState(false);
  const [careerAnalysis, setCareerAnalysis] = useState<CareerAnalysis | null>(null);

  const [careerFocusPreferences, setCareerFocusPreferences] = useState<string[]>([]);
  const [additionalContext, setAdditionalContext] = useState('');

  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<any>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<CampaignSuggestion | null>(null);

  const [campaignName, setCampaignName] = useState('');
  const [expertiseArea, setExpertiseArea] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [durationWeeks, setDurationWeeks] = useState(2);

  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [copied, setCopied] = useState<number | null>(null);

  useEffect(() => {
    loadResumes();
  }, []);

  async function loadResumes() {
    const { data } = await supabase
      .from('resume_uploads')
      .select('id, filename, created_at')
      .order('created_at', { ascending: false });

    if (data) {
      setResumes(data);
    }
  }

  async function analyzeResume() {
    if (!selectedResumeId) return;

    setAnalyzingResume(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-resume-career`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ resumeId: selectedResumeId }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to analyze resume');
      }

      const { analysis } = await response.json();
      setCareerAnalysis(analysis);
      setStep(2);
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.message);
    } finally {
      setAnalyzingResume(false);
    }
  }

  async function generateSuggestions() {
    if (!careerAnalysis) return;

    setGeneratingSuggestions(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-campaign-suggestions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            resumeAnalysisId: careerAnalysis.id,
            careerFocusPreferences,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate suggestions');
      }

      const { suggestions: suggestionsData } = await response.json();
      setSuggestions(suggestionsData);
      setStep(3);
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.message);
    } finally {
      setGeneratingSuggestions(false);
    }
  }

  function selectSuggestion(suggestion: CampaignSuggestion) {
    setSelectedSuggestion(suggestion);
    setCampaignName(suggestion.campaign_name);
    setExpertiseArea(suggestion.expertise_area);
    setTargetAudience(suggestion.target_audience);
  }

  async function generateCampaign() {
    if (!campaignName || !expertiseArea || !careerAnalysis) return;

    setGenerating(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-content-campaign`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            resumeId: selectedResumeId,
            campaignName,
            expertiseArea,
            targetAudience,
            durationWeeks,
            resumeAnalysisId: careerAnalysis.id,
            careerFocusPreferences,
            additionalContext,
            industryResearch: suggestions?.industry_research || {},
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate campaign');
      }

      const { posts: generatedPosts } = await response.json();
      setPosts(generatedPosts);
      setStep(4);
    } catch (error: any) {
      console.error('Error:', error);
      alert(error.message);
    } finally {
      setGenerating(false);
    }
  }

  function toggleCareerFocus(focusId: string) {
    setCareerFocusPreferences(prev =>
      prev.includes(focusId)
        ? prev.filter(id => id !== focusId)
        : [...prev, focusId]
    );
  }

  function copyPost(index: number) {
    const post = posts[index];
    const text = `${post.post}\n\n${post.hashtags.map(h => `#${h}`).join(' ')}`;
    navigator.clipboard.writeText(text);
    setCopied(index);
    setTimeout(() => setCopied(null), 2000);
  }

  function downloadCampaign() {
    let content = '========================================\n';
    content += `LINKEDIN CONTENT CAMPAIGN: ${campaignName}\n`;
    content += '========================================\n\n';
    content += `Expertise Area: ${expertiseArea}\n`;
    content += `Target Audience: ${targetAudience}\n`;
    content += `Duration: ${durationWeeks} weeks\n\n`;

    posts.forEach((post) => {
      content += `========================================\n`;
      content += `Week ${post.week} - ${post.day}\n`;
      content += `========================================\n\n`;
      content += post.post + '\n\n';
      content += post.hashtags.map(h => `#${h}`).join(' ') + '\n\n';
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `content-campaign-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function resetWorkflow() {
    setStep(1);
    setSelectedResumeId('');
    setCareerAnalysis(null);
    setCareerFocusPreferences([]);
    setAdditionalContext('');
    setSuggestions(null);
    setSelectedSuggestion(null);
    setCampaignName('');
    setExpertiseArea('');
    setTargetAudience('');
    setPosts([]);
  }

  const groupedPosts = posts.reduce((acc, post) => {
    if (!acc[post.week]) acc[post.week] = [];
    acc[post.week].push(post);
    return acc;
  }, {} as Record<number, Post[]>);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <Sparkles className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">AI-Powered Content Campaigns</h1>
          </div>
          <p className="text-gray-600">
            Create personalized LinkedIn campaigns (Monday, Wednesday, Thursday) based on your career profile and industry trends
          </p>
        </div>

        {step < 4 && (
          <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              {[
                { num: 1, label: 'Select Resume', active: step >= 1 },
                { num: 2, label: 'Set Preferences', active: step >= 2 },
                { num: 3, label: 'Review Suggestions', active: step >= 3 },
                { num: 4, label: 'Generate Campaign', active: step >= 4 },
              ].map((s, i) => (
                <div key={s.num} className="flex items-center flex-1">
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                        s.active
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {s.num}
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium ${
                        s.active ? 'text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      {s.label}
                    </span>
                  </div>
                  {i < 3 && (
                    <div
                      className={`flex-1 h-1 mx-4 ${
                        step > s.num ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Step 1: Select Your Resume
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose a resume to analyze
                </label>
                <select
                  value={selectedResumeId}
                  onChange={e => setSelectedResumeId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a resume...</option>
                  {resumes.map(resume => (
                    <option key={resume.id} value={resume.id}>
                      {resume.filename} - {new Date(resume.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={analyzeResume}
                disabled={!selectedResumeId || analyzingResume}
                className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {analyzingResume ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing Resume...
                  </>
                ) : (
                  <>
                    Analyze Resume
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {step === 2 && careerAnalysis && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Step 2: Career Profile & Preferences
              </h2>

              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-3">Your Career Profile</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 font-medium">Experience:</span> {careerAnalysis.years_experience} years
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Seniority:</span> {careerAnalysis.seniority_level}
                  </div>
                  <div className="col-span-2">
                    <span className="text-blue-700 font-medium">Industries:</span> {careerAnalysis.industries.join(', ')}
                  </div>
                  <div className="col-span-2">
                    <span className="text-blue-700 font-medium">Key Expertise:</span> {careerAnalysis.areas_of_expertise.slice(0, 5).join(', ')}
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  What aspects of your career do you want to emphasize? (Select all that apply)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {CAREER_FOCUS_OPTIONS.map(option => {
                    const Icon = option.icon;
                    const isSelected = careerFocusPreferences.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        onClick={() => toggleCareerFocus(option.id)}
                        className={`flex items-center p-4 rounded-lg border-2 transition ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mr-3 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                          {option.label}
                        </span>
                        {isSelected && <CheckCircle className="w-5 h-5 ml-auto text-blue-600" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Context (Optional)
                </label>
                <textarea
                  value={additionalContext}
                  onChange={e => setAdditionalContext(e.target.value)}
                  placeholder="Any specific goals or themes you want to focus on in this campaign?"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Back
                </button>
                <button
                  onClick={generateSuggestions}
                  disabled={generatingSuggestions || careerFocusPreferences.length === 0}
                  className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingSuggestions ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating AI Suggestions...
                    </>
                  ) : (
                    <>
                      Generate Campaign Ideas
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && suggestions && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Step 3: AI-Powered Campaign Suggestions
              </h2>

              {suggestions.industry_research && Object.keys(suggestions.industry_research).length > 0 && (
                <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center mb-3">
                    <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                    <h3 className="font-semibold text-green-900">Industry Research Insights</h3>
                  </div>
                  {suggestions.industry_research.trending_topics && (
                    <div className="text-sm text-green-800">
                      <strong>Trending Topics:</strong> {suggestions.industry_research.trending_topics.slice(0, 3).join(', ')}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4 mb-6">
                <h3 className="font-semibold text-gray-900">Choose a campaign idea:</h3>
                {suggestions.suggested_campaign_names?.map((idea: CampaignSuggestion, index: number) => (
                  <div
                    key={index}
                    onClick={() => selectSuggestion(idea)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                      selectedSuggestion?.campaign_name === idea.campaign_name
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-1">{idea.campaign_name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{idea.rationale}</p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            {idea.expertise_area}
                          </span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                            {idea.target_audience}
                          </span>
                        </div>
                      </div>
                      {selectedSuggestion?.campaign_name === idea.campaign_name && (
                        <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 ml-4" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={e => setCampaignName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expertise Area *
                    </label>
                    <input
                      type="text"
                      value={expertiseArea}
                      onChange={e => setExpertiseArea(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Audience
                    </label>
                    <input
                      type="text"
                      value={targetAudience}
                      onChange={e => setTargetAudience(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Duration
                  </label>
                  <div className="flex gap-3">
                    {[2, 3, 4].map(weeks => (
                      <button
                        key={weeks}
                        onClick={() => setDurationWeeks(weeks)}
                        className={`flex-1 px-4 py-3 rounded-lg font-medium transition ${
                          durationWeeks === weeks
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {weeks} Weeks
                        <div className="text-xs opacity-80 mt-1">{weeks * 3} posts</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Back
                </button>
                <button
                  onClick={generateCampaign}
                  disabled={!campaignName || !expertiseArea || generating}
                  className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating Campaign...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Campaign
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 4 && posts.length > 0 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Your Campaign is Ready!</h2>
              <div className="flex gap-3">
                <button
                  onClick={downloadCampaign}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download
                </button>
                <button
                  onClick={resetWorkflow}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition"
                >
                  Create New
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {Object.entries(groupedPosts).map(([week, weekPosts]) => (
                <div key={week} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                    <h3 className="text-lg font-bold text-gray-900">Week {week}</h3>
                  </div>
                  <div className="space-y-4">
                    {weekPosts.map((post, i) => {
                      const globalIndex = posts.indexOf(post);
                      return (
                        <div key={i} className="border-l-4 border-blue-600 pl-4">
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="font-bold text-gray-900">{post.day}</h4>
                            <button
                              onClick={() => copyPost(globalIndex)}
                              className="flex items-center text-sm text-blue-600 hover:text-blue-700"
                            >
                              {copied === globalIndex ? (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <Copy className="w-4 h-4 mr-1" />
                                  Copy
                                </>
                              )}
                            </button>
                          </div>
                          <p className="text-gray-700 text-sm mb-2 whitespace-pre-wrap">{post.post}</p>
                          <div className="flex flex-wrap gap-1">
                            {post.hashtags.map((tag, j) => (
                              <span key={j} className="text-xs text-blue-600">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
